<template>
  <section v-if="view.book" class="book-day-box">
    <h2 class="section-title">{{ title }}</h2>
    <router-link
      :to="view.link"
      class="book-day"
      :class="{ 'book-day--safari': isSafari }"
    >
      <div class="book-day__img-box">
        <div v-if="view.book.has_audio" class="book-day__play">
          <span class="book-day__play-icon iconfont iaudio"></span>
        </div>

        <BookCover
          :doc="view.book"
          coverClass="book-day__img"
          fallbackCoverClass="book-day__img-fallback"
        />
      </div>
      <div class="book-day__text-box">
        <p class="book-day__category">
          {{ view.category }}
        </p>
        <h3 class="book-day__title">
          {{ view.book.title }}
        </h3>
        <div class="book-day__text" v-html="view.description"></div>
        <h4 class="book-day__author">By {{ view.book.author }}</h4>
      </div>
      <BookCardBackground v-if="!isSafari" :doc="view.book" />
    </router-link>
  </section>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'

import { BookOfTheDayView } from '@/models/doc.book'
import { platform } from '@/services/platform'
import { isSafari as _isSafari } from '@/helpers/userAgent'

import BookCover from '@/app/components/book/card/BookCover.vue'
import BookCardBackground from '@/app/components/book/card/BookCardBackground.vue'

defineProps<{
  title: string
  view: BookOfTheDayView
}>()

const isSafari = ref(false)

onBeforeMount(async () => {
  isSafari.value = (await platform.isIOS()) || _isSafari()
})
</script>

<style lang="scss" scoped>
.book-day {
  position: relative;
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding: 47px 87px;
  border: 1px solid transparent;
  overflow: hidden;
  @media only screen and (max-width: $w-tablet-s) {
    padding: 47px 71px;
  }
  @media only screen and (max-width: $w-medium-s) {
    padding: 39px 31px;
    border-radius: 0;
    border: none;
    align-items: start;
    @include full-width-no-padding;
  }
  @include hover {
    color: $c-black-2;
    border-color: $c-black-13;

    .book-day__img-box {
      transform: scale(1.07);
    }
  }
  &__img-box {
    flex-shrink: 0;
    width: 140px;
    margin-right: 48px;
    border-radius: 15px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.11);
    transition: all 0.2s ease-in-out;
    position: relative;
    @media only screen and (max-width: $w-medium-s) {
      width: 97px;
    }
    @media only screen and (max-width: $w-mobile-s) {
      margin-right: 32px;
      border-radius: 4px;
    }
  }
  &__img {
    display: block;
    border-radius: 15px;
    max-width: var(--cover-width);
    @media only screen and (max-width: $w-mobile-s) {
      border-radius: 4px;
    }
  }
  &__img,
  &__img-fallback {
    --cover-width: 200px;
    @media only screen and (max-width: $w-tablet-s) {
      --cover-width: 152px;
    }
    @media only screen and (max-width: $w-medium-s) {
      --cover-width: 93px;
    }
  }
  &__play {
    position: absolute;
    top: calc(var(--container-padding-negative) + 1px);
    left: calc(var(--container-padding-negative) + 1px);
    border-radius: 50%;
    background-color: $c-white;
    box-shadow: 0px 2px 6px rgba(20, 28, 49, 0.1);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    @media only screen and (min-width: $w-medium-b) {
      display: none;
    }
    &-icon {
      font-size: 18px;
    }
  }
  &__category {
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $c-black-6;
    margin-bottom: 24px;
    @media only screen and (max-width: $w-tablet-s) {
      margin-bottom: 20px;
    }
    @media only screen and (max-width: $w-medium-s) {
      font-size: 10px;
      line-height: 12px;
      margin-bottom: 10px;
    }
  }
  &__title {
    font-weight: 500;
    font-size: 24px;
    line-height: 30px;
    text-transform: capitalize;
    margin-bottom: 3px;
    @media only screen and (max-width: $w-tablet-s) {
      margin-bottom: 12px;
    }
    @media only screen and (max-width: $w-medium-s) {
      font-size: 16px;
      line-height: 20px;
      margin-bottom: 4px;
    }
  }
  &__text {
    font-weight: 400;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: -0.02em;
    color: $c-black-15;
    margin-bottom: 26px;
    p {
      color: $c-black-15;
      @media only screen and (max-width: $w-medium-s) {
        font-size: 12px;
        line-height: 16px;
      }
    }
    a {
      text-decoration-line: underline !important;
      color: $c-black-3 !important;
    }
    @media only screen and (max-width: $w-tablet-s) {
      margin-bottom: 20px;
    }
    @media only screen and (max-width: $w-medium-s) {
      margin-bottom: 12px;
    }
  }
  &__author {
    font-size: 16px;
    line-height: 22px;
    color: $c-black-15;
    font-weight: 400;
    @media only screen and (max-width: $w-tablet-s) {
      margin-bottom: 24px;
    }
    @media only screen and (max-width: $w-medium-s) {
      font-size: 12px;
      line-height: 18px;
      margin-bottom: 0px;
    }
  }
}

.book-day--safari {
  background: $c-black-5;
}

.night-mode .book-day {
  border-color: $c-dark-1;
  @include hover {
    color: $c-white;
    border-color: $c-border-2;
  }
  &__play {
    background-color: $c-dark-1;
    color: $c-black-11;
  }
  &__category {
    color: $c-dark-8;
  }
  &__text {
    color: $c-dark-6 !important;
    :deep(p) {
      color: $c-dark-6;
    }
  }
  &__author {
    color: $c-black-11;
  }
}

.night-mode .book-day--safari {
  background: $c-dark-2;
}
</style>
